// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import allLocales from '@fullcalendar/core/locales-all';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import SidebarHeader from 'OnLabor/workboard/calendar/sidebar-header';
import SwitchInput from 'Ui/components/common/form/switch-input';
import TodoList from 'OnLabor/workboard/common/todo-list';
import {updateWorkboardLayers} from 'Store/actions/workboard';
import {DATE_FORMATS} from 'Utils/time-utils';
import {getLanguagePreference} from 'Utils/unit-conversion-utils';
import {getWeatherIcon} from 'Utils/weather-utils';
import {AVAILABILITY, WORKBOARD} from 'OnLabor/workboard/constants/event-types';
import {ONLINK_WHITE, JD_GREEN_THEME, SECONDARY, TEXT_PRIMARY, INFO} from 'Ui/constants/theme';
import {PRO_GOLF_LABOR_RESKIN, PRO_GOLF_GROUP_SMS} from 'Common/constants/isg-toggles';
import moment from 'moment';
import {MANAGE_TIMEOFF} from 'Common/constants/business-activities';
import {Accordion, AccordionDetails, AccordionSummary} from '@deere/isg.component-library';
import Messaging from '../common/messages';
import {useHistory} from 'react-router-dom';
import {LAST_DAY_FOR_OLD_WORKBOARD_FLOW} from '../constants/workboard-steps';

const SHARED_WIDGET_PROPS = {
    bgcolor: ONLINK_WHITE,
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: '5px'
};

function renderEvent(eventInfo, isLaborReskinEnabled) {
    const {
        extendedProps,
        start,
        title
    } = eventInfo.event;

    if (extendedProps.forecast) {
        return (
            <>
                <img
                    alt={extendedProps.forecast.icon_code.toString()}
                    className='workboard-weather-icon'
                    src={getWeatherIcon(extendedProps.forecast.icon_code)}
                />
                <i>{title}</i>
            </>
        );
    }

    if (extendedProps.employeeStatusOption) {
        return (
            <div className='centered-item'>
                {title}
            </div>
        );
    }

    return (
        <>
            {isLaborReskinEnabled ? moment(start).format('h:mma') : <b>{moment(start).format('h:mma')}</b>}
            {` ${title}`}
        </>
    );
}

function onLayerChange(name, value, updateLayersState, layers, appUserId) {
    updateLayersState({
        ...layers,
        [name]: value
    });

    if (name !== 'showTimeOff') {
        return;
    }

    const showTimeOff = localStorage.getItem(`${appUserId}_showTimeOff`);

    if (showTimeOff !== value.toString()) {
        localStorage.setItem(`${appUserId}_showTimeOff`, value.toString());
    }
}

function Calendar(props) {
    const {
        appUserId,
        availabilityByDate,
        fetchAvailabilityByDate,
        fetchWorkboards,
        isgToggles,
        isMigrated,
        layers,
        loading,
        membershipId,
        myJdPermissions,
        openAddTimeOffDialog,
        openAddWorkboardDialog,
        setStartAndEndTime,
        todoTemplates,
        translations,
        updateLayersState,
        weather,
        workboards
    } = props;

    const history = useHistory();

    const calendarRef = React.useRef();

    const loadingApplication = loading.workboards || loading.todoList || loading.availability;

    React.useEffect(() => {
        const showTimeOff = localStorage.getItem(`${appUserId}_showTimeOff`) === 'true';

        if (showTimeOff) {
            onLayerChange('showTimeOff', showTimeOff, updateLayersState, layers, appUserId);
        }
    }, []);

    const sidebarComponentProps = {
        ...SHARED_WIDGET_PROPS,
        className: 'sidebar-group-box'
    };

    const sidebarStackProps = {
        padding: '4px 12px 12px'
    };

    const eventSources = React.useMemo(() => {
        const baseSources = [
            {
                display: layers.showTimeOff ? 'block' : 'none',
                events: availabilityByDate,
                textColor: ONLINK_WHITE
            },
            {
                display: 'list-item',
                events: weather,
                textColor: '#000'
            }
        ];

        if (!isgToggles[PRO_GOLF_LABOR_RESKIN]) {
            baseSources.unshift({
                color: JD_GREEN_THEME,
                display: layers.showWorkboards ? 'block' : 'none',
                events: workboards,
                textColor: ONLINK_WHITE
            });
            return baseSources;
        }

        const [workboardsFromOldFlow, published, drafts] = workboards.reduce(
            (acc, wb) => {
                if (wb.dateSelect < LAST_DAY_FOR_OLD_WORKBOARD_FLOW) {
                    acc[0].push(wb);
                } else if (!wb.isDraft) {
                    acc[1].push(wb);
                } else {
                    acc[2].push(wb);
                }
                return acc;
            },
            [[], [], []]
        );

        baseSources.unshift({
            color: INFO,
            display: layers.showWorkboards ? 'block' : 'none',
            events: workboardsFromOldFlow,
            textColor: TEXT_PRIMARY
        });

        baseSources.unshift({
            color: SECONDARY,
            display: layers.showWorkboards ? 'block' : 'none',
            events: drafts,
            textColor: TEXT_PRIMARY
        });
        baseSources.unshift({
            color: JD_GREEN_THEME,
            display: layers.showWorkboards ? 'block' : 'none',
            events: published,
            textColor: ONLINK_WHITE
        });

        return baseSources;
    }, [workboards, isgToggles, layers, weather, availabilityByDate]);

    const AccordionVariant = () => (
        <Accordion className='workboard_accordion'>
            <AccordionSummary>
                {translations.ONLINK_CALENDAR_LAYERS}
            </AccordionSummary>
            <AccordionDetails className='workboard_accordionContent'>
                <SwitchInput
                    checked={layers.showWorkboards}
                    name='showWorkboards'
                    onChange={(name, checked) => onLayerChange(name, checked, updateLayersState, layers)}
                    onLabel={translations.ONLINK_WORKBOARDS}
                    translations={translations}
                />
                {
                    (!isMigrated || myJdPermissions[MANAGE_TIMEOFF]) &&
                    <SwitchInput
                        checked={layers.showTimeOff}
                        disabled={loading.availability}
                        name='showTimeOff'
                        onChange={(name, checked) => onLayerChange(name, checked, updateLayersState, layers, appUserId)}
                        onLabel={translations.ONLINK_TIME_OFF}
                        translations={translations}
                    />
                }
            </AccordionDetails>
        </Accordion>
    );

    return (
        <Stack
            className='workboard-container'
            direction='row'
            flex={1}
            overflow='hidden'
        >
            <LoadingWrapper
                alwaysRenderChildren={true}
                className='dashboard-loading-icon'
                loading={loadingApplication}
                size='50px'
            >
                <Box
                    {...SHARED_WIDGET_PROPS}
                    className='workboard-calendar'
                    padding='12px'
                    visibility={loadingApplication ? 'hidden' : 'visible'}
                    width='80%'
                >
                    <FullCalendar
                        contentHeight='100%'
                        customButtons={{
                            prev: {
                                click() {
                                    const calendarInstance = calendarRef.current.getApi();

                                    calendarInstance.prev();

                                    setStartAndEndTime({
                                        start: moment(calendarInstance.view.activeStart).format(DATE_FORMATS.day),
                                        end: moment(calendarInstance.view.activeEnd).format(DATE_FORMATS.day)
                                    });
                                }
                            },
                            next: {
                                click() {
                                    const calendarInstance = calendarRef.current.getApi();

                                    calendarInstance.next();

                                    setStartAndEndTime({
                                        start: moment(calendarInstance.view.activeStart).format(DATE_FORMATS.day),
                                        end: moment(calendarInstance.view.activeEnd).format(DATE_FORMATS.day)
                                    });
                                }
                            }
                        }}
                        eventClick={({event}) => {
                            if (event.extendedProps.type === WORKBOARD) {
                                isgToggles[PRO_GOLF_LABOR_RESKIN] ? history.push(`/labor/workboard/${event.id}`) :
                                    openAddWorkboardDialog({
                                        onClose: fetchWorkboards,
                                        workboardId: event.id,
                                        workboards
                                    });
                            } else if (event.extendedProps.type === AVAILABILITY) {
                                openAddTimeOffDialog({
                                    onClose: fetchAvailabilityByDate,
                                    timeOffValues: {
                                        ...event.extendedProps,
                                        toDate: moment(event.endStr).subtract(1, 'day').format(DATE_FORMATS.day)
                                    }
                                });
                            }
                        }}
                        eventContent={(event) => renderEvent(event, isgToggles[PRO_GOLF_LABOR_RESKIN])}
                        eventOrder='sortIndex'
                        eventSources={eventSources}
                        eventTextColor={ONLINK_WHITE}
                        headerToolbar={{
                            left: 'prev,title,next',
                            right: 'dayGridMonth,dayGridWeek'
                        }}
                        height='100%'
                        locale={getLanguagePreference()}
                        locales={allLocales}
                        nextDayThreshold='12:00:00'
                        plugins={[dayGridPlugin, listPlugin]}
                        ref={calendarRef}
                    />
                </Box>
            </LoadingWrapper>
            <Stack
                className='workboard-sidebar'
                direction='column'
                overflow='auto'
            >
                <Box {...sidebarComponentProps}>
                    {isgToggles[PRO_GOLF_GROUP_SMS] ? AccordionVariant() : (
                        <>
                            <SidebarHeader title={translations.ONLINK_CALENDAR_LAYERS}/>
                            <Stack
                                {...sidebarStackProps}
                                direction='column'
                                spacing='20px'
                            >
                                <SwitchInput
                                    checked={layers.showWorkboards}
                                    className='layer-input'
                                    name='showWorkboards'
                                    onChange={(name, checked) => onLayerChange(name, checked, updateLayersState, layers)}
                                    onLabel={translations.ONLINK_WORKBOARDS}
                                    translations={translations}
                                />
                                {
                                    (!isMigrated || myJdPermissions[MANAGE_TIMEOFF]) &&
                                    <SwitchInput
                                        checked={layers.showTimeOff}
                                        className='layer-input'
                                        disabled={loading.availability}
                                        name='showTimeOff'
                                        onChange={(name, checked) => onLayerChange(name, checked, updateLayersState, layers, appUserId)}
                                        onLabel={translations.ONLINK_TIME_OFF}
                                        translations={translations}
                                    />
                                }
                            </Stack>
                        </>
                    )}
                </Box>
                <Box {...sidebarComponentProps}>
                    <TodoList
                        appUserId={appUserId}
                        isgToggles={isgToggles}
                        membershipId={membershipId}
                        todoTemplates={todoTemplates}
                        translations={translations}
                    />
                </Box>
                {isgToggles[PRO_GOLF_GROUP_SMS] && (
                    <Box {...sidebarComponentProps}>
                        <Messaging
                            translations={translations}
                        />
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

Calendar.propTypes = {
    appUserId: PropTypes.string,
    availabilityByDate: PropTypes.arrayOf(PropTypes.object),
    fetchAvailabilityByDate: PropTypes.func,
    fetchWorkboards: PropTypes.func,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    layers: PropTypes.object,
    loading: PropTypes.object,
    membershipId: PropTypes.string,
    myJdPermissions: PropTypes.myJdPermissions,
    openAddTimeOffDialog: PropTypes.func,
    openAddWorkboardDialog: PropTypes.func,
    setStartAndEndTime: PropTypes.func,
    todoTemplates: PropTypes.arrayOf(PropTypes.object),
    translations: PropTypes.translations,
    updateLayersState: PropTypes.func,
    weather: PropTypes.arrayOf(PropTypes.object),
    workboards: PropTypes.arrayOf(PropTypes.object)
};

export function mapStateToProps(state) {
    return {
        appUserId: state.account.appUserId,
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        updateLayersState(props) {
            dispatch(updateWorkboardLayers(props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
